/**
 * What an install is configured with — the application settings, the layer a
 * workspace draws over them, and the two resolved together (§4.1).
 *
 * The types and their validators live here rather than in the desktop app
 * because the account area writes the same documents: an organization's
 * defaults, and the configuration a project it shares carries (§5.25). One
 * definition means a value the app refuses is a value the page and the Worker
 * refuse too.
 */
import type { RulesRepoRef } from '../rules/repository';
import type { MemberAgents } from '../org/policy';
import type { MemberExtensions } from '../org/extension-library';
import type { AgentExtensions } from './extensions';
import type { AgentId } from './agents';
import type { GitFormats } from './git-formats';
import type { HostService } from './hostservice';
import type { LocalService } from './localservice';
import type { WritingLanguages } from './languages';
import type { NotifyMode, ProbeMode } from './modes';
import type { WorkspaceTarget } from './targets';
import type { MemberTargets } from '../org/targets-policy';

/**
 * Each agent's own settings as this install sets them (§5.8): one document per
 * agent, shaped exactly like the file that agent reads, holding only the keys
 * somebody set. The catalogue of what may go in one is `./agent-settings`.
 */
export type AgentSettings = Record<AgentId, Record<string, unknown>>;

/**
 * Each agent's host config as a session would receive it — scrubbed, without
 * the host's own commands — or null where the agent has no config file. What a
 * row of the settings page inherits when nothing is set (§5.8).
 */
export type HostAgentSettings = Record<AgentId, Record<string, unknown> | null>;

/** Whether each hand-back is audited, and which findings the notice under the terminal names (`AppSettings.audit.auto`). */
export const AUTO_AUDITS = ['off', 'serious', 'all'] as const;
export type AutoAudit = (typeof AUTO_AUDITS)[number];
/** Which rules broken hold a merge: an enforced MUST alone, any MUST, a SHOULD too, or none. */
export const BLOCK_RULES = ['enforced', 'must', 'should', 'none'] as const;
export type BlockRules = (typeof BLOCK_RULES)[number];
/** Which of the map's findings, left unsettled, hold a merge. */
export const BLOCK_FINDINGS = ['high', 'medium', 'none'] as const;
export type BlockFindings = (typeof BLOCK_FINDINGS)[number];
export interface AuditSettings {
  auto: AutoAudit;
  mapModel: string;
  mapEffort: string;
  blockRules: BlockRules;
  blockFindings: BlockFindings;
}

/** The steps that land a finished session by themselves (`AppSettings.autoLand`). */
export interface AutoLand {
  branch: boolean;
  message: boolean;
  push: boolean;
  pullRequest: boolean;
  /** `stop` parks the machine, `close` closes the session; `off` leaves it open. */
  session: 'off' | 'stop' | 'close';
}

/**
 * The code editor's preferences (§5.4). Application-wide and never per
 * workspace: a key binding or a soft wrap is how a person reads code, whatever
 * the code is.
 */
export interface EditorPrefs {
  /**
   * `standard` is CodeMirror's own keys, `vscode` VS Code's, `intellij`
   * IntelliJ's for the same commands, `vim` modal editing.
   */
  keymap: 'standard' | 'vscode' | 'intellij' | 'vim';
  /** Long lines wrap at the pane's edge rather than scrolling sideways. */
  wrap: boolean;
  /** Spaces and tabs drawn as faint marks. */
  whitespace: boolean;
  /** A rule down each indentation level. */
  indentGuides: boolean;
  /** The whole file, small, beside the scrollbar. */
  minimap: boolean;
  /** The project's formatter runs over the buffer each time it is saved. */
  formatOnSave: boolean;
  /** Language servers start by themselves when a file of their language opens. */
  languageServers: boolean;
}

/* ── Egress (§7.1) ──────────────────────────────────────────────────────── */

/** How a session answers a host that is not on its allowlist. */
export type EgressMode = 'off' | 'ask' | 'auto';

/**
 * Application-wide settings — the baseline every workspace inherits (§4.1).
 * Edited in the application settings view (opened from the menu bar), stored as
 * one row in `settings`, clamped host-side — the renderer never writes an
 * unvalidated value.
 */
export interface AppSettings {
  /* General */
  /**
   * `system`, or the id of one of the desktop's themes (`light`, `dark`,
   * `dracula`, …). A string rather than a union because the themes are the
   * desktop's registry, which this package never sees: the validator here
   * keeps only the shape of an id, and the app falls back to `dark` for one it
   * does not ship — a theme it dropped, or one a newer version wrote.
   */
  appearance: string;
  confirmDestroy: boolean;
  /**
   * Whether the first-launch setup has been walked through (§9.4). It is the
   * one setting a person never sets by hand: the setup's last step writes it,
   * and the shell reads it to decide whether a launch with no workspace opens
   * on the setup or straight on the workspace wizard. Stored rather than kept
   * in the window because a first launch is a fact about the install, and a
   * flag in `localStorage` would run the setup again in every window profile.
   */
  setupDone: boolean;
  /**
   * Whether the machine is kept from sleeping while a session works (§11.4).
   *
   * A session's micro-VM is suspended with the machine, so a laptop that idles
   * into sleep stops the agent mid-task. On, one power-save blocker is held
   * while any session is `STARTING` or `RUNNING` and released when the last
   * hands its work back; the screen may still go dark. It stops the idle sleep
   * only — a closed lid or a sleep chosen from the menu still wins.
   *
   * On by default: an agent left to work is the point, and finding it stalled
   * behind a lid is the surprise. Application-wide and not overridable: it is
   * a decision about this machine, not about one workspace's work.
   */
  preventSleep: boolean;
  /** How the code editor behaves (§5.4) — the person's own habits, not a workspace's. */
  editor: EditorPrefs;

  /* Session defaults (§6.1) */
  /**
   * The agents this install uses (§5.10). Both by default; never empty, since an
   * install with no agent cannot start a session at all.
   *
   * Application-wide rather than per workspace: it says which CLIs are on this
   * machine and which ones you want to be offered, which is a fact about the
   * install, not about the code being worked on. Every surface that offers a
   * model reads it — the task form, a workflow step, the defaults below — so
   * turning one off takes its models out of the app rather than leaving them to
   * be picked and then fail at launch.
   *
   * What it does *not* do is stop work already written: a task or a step that
   * names a disabled agent's model still runs it, because the alternative is a
   * queued task silently doing something other than what it says.
   */
  agents: AgentId[];
  defaultModel: string;
  defaultEffort: string;
  defaultSourceBranch: string;
  /**
   * How a new task is worked when the form opens (§5.15): `''` one
   * instruction, `agent` the agent proposes its workflow, or the id of a
   * workflow of the library. A prefill — the form still says otherwise per task
   * — and an id the library no longer has reads as one instruction.
   */
  defaultWorkflowId: string;
  /**
   * Accept the workflow the agent proposes when a task asked it to (§5.15) —
   * the Review a person otherwise presses before the run goes on.
   */
  autoAcceptPlan: boolean;
  /**
   * The audit of a session's change (§6.3, §5.27). `auto`: run on its own at
   * each hand-back — the team's Rules read against it, and the Change Map's
   * findings and the rules it breaks shown to the person, who ignores the ones
   * not worth fixing and sends the rest (never sent on their own); `serious` names
   * the High and Medium ones under the agent's terminal, `all` every one, and the
   * Change Map lists them all either way; `off` leaves both to
   * a person's Audit and Send, and an unsettled High holds the merge unless it
   * is `off`. `mapModel` / `mapEffort`: the pair the Change Map is drawn with,
   * `''` for Airlock's own pick. `blockRules` / `blockFindings`: what holds a
   * merge — the rules broken of which levels, the map's findings left
   * unsettled of which severities (the latter only while the audit runs).
   */
  audit: AuditSettings;
  /**
   * How many sessions may be *working* at once — `STARTING` or `RUNNING`
   * (§11.4). A session whose task is finished but which is still open for review
   * does not count against this: reviewing carefully must not cost the pipeline
   * its width, which is what the single old ceiling made it cost.
   *
   * It bounds **dispatch**, and is deliberately not an invariant. Sending review
   * notes back puts a session to work again (§6.6) and may cross it from below —
   * refusing to answer an annotation because the queue is full would be absurd.
   */
  maxWorkingSessions: number;
  /**
   * How many sandboxes may exist at once, whatever they are doing (§11.4).
   *
   * The other half of the pair, and the one that keeps the first honest: a
   * running micro-VM holds its `vmMemoryMb` whether or not an agent is thinking
   * in it, and destroying one automatically is refused — a sandbox is not a
   * cache entry. A *parked* one (`parkAfterMinutes`) holds no memory and is not
   * counted. Without this the board would fill the machine with sessions
   * waiting to be reviewed. Reached, the board stops dispatching and says why
   * rather than stalling in silence.
   */
  maxLiveSandboxes: number;
  /**
   * How long a session that has handed its work back may sit untouched before
   * its micro-VM is stopped, in minutes — 0 never parks one (§11.4).
   *
   * Stopping rather than destroying is what makes it safe to do unasked: the
   * disk is kept, the work on it is not touched, and opening the session boots
   * the same machine again with the agent's conversation resumed. What it gives
   * back is the memory, which is the scarce thing on a laptop running three
   * agents. App-wide for the ceilings' reason: it is a decision about this
   * machine, not about one workspace's work.
   */
  parkAfterMinutes: number;
  /**
   * How long an archived task is kept before it is deleted (§5.20), in days.
   * The window opens at `archivedAt`.
   */
  archiveRetentionDays: number;

  /* Sandbox (§5.1, §10) */
  sandboxBackend: 'auto' | 'local';
  /** The session image, as a Dockerfile Airlock replays into a snapshot (§10.1). */
  dockerfile: string;
  /** An optional in-guest container runtime for integration tests (§5.1). */
  containerRuntime: 'none' | 'docker';
  vmCpu: number;
  vmMemoryMb: number;
  vmDiskGb: number;
  /** Dedicated ext4 disk for Docker's data root; used only when Docker is enabled. */
  dockerDiskGb: number;

  /**
   * How many warm images this machine keeps (§8.6).
   *
   * Each one is a `vmDiskGb` disk carrying a workspace's installed
   * dependencies, so this is a disk budget and not a preference. The newest of
   * each workspace's comes first — a lockfile moving in one workspace can then
   * never take another's image away — and what is left of the count goes to the
   * most recently used of the rest.
   *
   * Not overridable: a workspace cannot answer for how much of the machine's
   * disk every *other* workspace is allowed.
   */
  warmImagesKept: number;
  /**
   * The most history, in MB, a micro-VM session carries in per repository
   * (§5.4): the git bundle of the source branch, refused above this before it
   * is copied. Not overridable: the copy runs in this machine's main process,
   * so what it can hold is the machine's to say, not a workspace's.
   */
  repoSeedMaxMb: number;
  /**
   * Where the Agent panel's turns run (owner, 2026-10-26): a throwaway micro-VM
   * (`sandbox`), or this machine (`worktree`) — the repositories copied into a
   * folder of their own with a private HOME, the agent's own login, no
   * isolation. Not overridable: a turn is about the app, not a workspace.
   */
  agentPanelPlacement: 'sandbox' | 'worktree';

  /* Agent (§5.8, §5.6, §9.2) */
  autonomous: boolean;
  agentConfigSource: 'host' | 'none';
  /**
   * Whether a session updates `claude` to the newest release on boot, or runs
   * the version its session image was built with (§10.2).
   *
   * `pin` is not "never update" — it is "update when you edit the image", which
   * is the reproducible half: a Dockerfile is a text, and a new text is a new
   * snapshot. `latest` trades that for currency at the cost of a network round
   * trip per boot, and it is skipped outright on the local backend, where the
   * guest's `claude` is the host's own install.
   */
  agentUpdate: 'pin' | 'latest';
  idleHintMs: number;
  /**
   * Every setting of each agent's own config this install sets (§5.8) — Claude
   * Code's `settings.json` and Codex's `config.toml`, one sparse document each.
   * A key left out keeps the host config's value where that is reused, and the
   * CLI's default where it is not.
   *
   * Laid over the agent's config whenever a session is provisioned, in a
   * micro-VM or on this machine alike, and under Airlock's own keys: the status
   * hooks, the workspace's trust, the ceilings placement imposes (§5.19). What
   * Airlock answers for is listed on the page with the reason, and refused here.
   *
   * Application-wide and not overridable: it is how the person likes the agent
   * to behave, and a repository with an opinion already carries one of its own —
   * a `.claude/settings.json` or a `.codex/` travels in the bundle.
   */
  agentSettings: AgentSettings;

  /**
   * Claude Code's status line, the script itself (`agent-settings/status-line.ts`):
   * written into every session at `STATUS_LINE_SCRIPT` while `agentSettings`'s
   * `statusLine` points at it. Empty is none. Application-wide, like the
   * document it completes.
   */
  statusLineScript: string;

  /**
   * What a session's agent is given **besides** that config (§5.26): the MCP
   * servers it may call, the skills it may use, the plugins it loads.
   *
   * Overridable per workspace, unlike `agentSettings` above, and the difference
   * is what each answers. That one is how a person likes the agent to behave,
   * which no set of repositories has an opinion about. This one is what the
   * *work* needs it to be able to do — a workspace of terraform repositories
   * and one of design systems want different tools — so it layers like every
   * other setting and travels in an organization's project (§5.25).
   */
  agentExtensions: AgentExtensions;

  /**
   * The language each thing an agent writes for a person is asked in — the
   * commit message, the workflow composer's steps, the Change Map. Application-
   * wide and not overridable: it is how this person reads, not what a set of
   * repositories needs.
   */
  writingLanguages: WritingLanguages;

  /**
   * Personal data masked out of what Airlock itself hands an agent (§7.5) —
   * the prompts typed through its composers, a task's description, an imported
   * ticket, a review note, a workflow step and the check output it carries —
   * each email, phone number, IBAN, card, NIR, public IP address and listed
   * term replaced by a stable placeholder. It cannot reach what the agent reads
   * for itself: the repository's files are the agent's to open.
   *
   * Off by default, overridable: a workspace whose repositories serve
   * customer data is the judgement about the code that turns it on.
   */
  privacyMask: boolean;
  /**
   * Names and other terms masked as `[TERM_…]` while `privacyMask` is on — a
   * client's name, a colleague's. Not an override: it is what this person
   * knows to be personal, whichever workspace it turns up in — a workspace
   * adds its own (`WorkspaceConfig.privacyTerms`), it never replaces these.
   */
  privacyTerms: string[];

  /* Git & merge (§5.4) */
  commitCoAuthoredBy: boolean;
  /**
   * Sync a session by itself when its agent hands a turn back and the host's
   * source branch has moved (§5.5) — the sync the review would otherwise ask
   * for before a merge. A rebase that would stop on conflicts is abandoned at
   * once, so an automatic sync never leaves a session mid-rebase.
   */
  autoSync: boolean;
  /**
   * What happens by itself once a session's run has ended (§5.5, §5.16) — each
   * step the person would otherwise take in the merge dialog, in its order: a
   * branch named after the task, the commit's title and description asked of
   * the agent, the merge, the push, the pull request, and the session stopped
   * or closed. Each one only once the one before it is on. Overridable: a
   * workspace whose repositories take a pull request for every change lands
   * further than one where a person merges (`SparseOverride`).
   */
  autoLand: AutoLand;
  /**
   * Whether sessions may be created from a pull request, and merged back onto
   * one (§5.16). Off is a real off: nothing is downloaded, no provider credential
   * is read, and the surfaces that would offer it are absent rather than
   * disabled — there is nothing to enable from inside them.
   */
  gitEnabled: boolean;

  /* Tunnels (§5.17) */
  /**
   * Whether a session's published port may be given a public URL (§5.17).
   *
   * **Off by default**, unlike the pull request integration: this is the second
   * deliberate hole in the isolation boundary (§5.9 is the first), and one
   * nobody should acquire by upgrading. Off is a real off — no agent is looked
   * for, no credential is read, and the controls that would offer one are absent
   * rather than disabled.
   */
  tunnelsEnabled: boolean;
  /**
   * The tunnel agent to run, when it is not where Airlock looks. App-wide and
   * not overridable: it names a file on *this machine*, which is not something a
   * workspace has an opinion about — and Airlock never installs one (§5.17).
   */
  tunnelBinPath: string;

  /* Task sources (§5.21) */
  /**
   * Whether Airlock imports work from a ticket tracker at all. Off is a real
   * off: no timer runs, no credential is read, and nothing reaches a provider.
   *
   * App-wide and **not overridable**, for `tunnelBinPath`'s reason one step
   * further out — it names an account and a site belonging to the *person*, not
   * to a repository set. Which workspace a given project's tickets land in is
   * the mapping's job, and that is a row rather than a setting because there is
   * one per project.
   */
  jiraEnabled: boolean;
  /** The Jira Cloud site, as a bare host: `example.atlassian.net`. */
  jiraHost: string;
  /** The account the API token was issued to — half of the Basic credential. */
  jiraEmail: string;
  /**
   * Whether cards assigned to you in Trello are imported (§5.21). No host
   * beside it: Trello is one site, so the provider *is* the host — and no
   * account either, because the other half of its credential is an API key,
   * which is stored where the token is rather than in a setting.
   */
  trelloEnabled: boolean;
  /**
   * Whether the bookmarklet is offered at all (§5.24) — the one integration
   * that has no account behind it: a page open in a browser becomes a task in
   * one click, and nothing is signed in to for that to work.
   *
   * On by default, because there is nothing to set up and nothing leaves the
   * machine. What it is here *for* is the other direction: an organization can
   * decide it is not offered, and then it is absent rather than disabled — the
   * same real off `gitEnabled` and `tunnelsEnabled` mean.
   */
  bookmarkletEnabled: boolean;
  /**
   * Whether this install keeps its usage statistics — one row per day of what
   * the agents spent (turns, tokens, an API-priced cost per model and effort)
   * and what came of the work (tasks started, landed, failed, re-run).
   *
   * On by default, because the rows never leave the machine on their own: a
   * personal install only ever shows them on its own Statistics page. An
   * organization's install sends its days to the account area only when the
   * organization publishes this setting on (`usage-sync.ts` on the desktop side)
   * — holding it, locked, is how an organization turns collection on or off for
   * every member, and an organization that says nothing collects nothing.
   */
  usageStatsEnabled: boolean;
  /**
   * Whether this install sends its organization a record of each run in that
   * organization's workspaces — the agent, model and effort, when it started
   * and ended, what came of it, its counts (turns, tool calls, files and lines
   * landed), the domains it was refused or allowed, its pull requests and an
   * estimate of its tokens' cost. Never a prompt, a reply, a file, a command or
   * a secret (`@airlock/contracts/runs/record`).
   *
   * Off by default, and nothing is sent unless the organization that owns the
   * workspace publishes it on as well (`run-trail.ts` on the desktop side): an
   * organization turns it on in its onboarding, and holding it, locked, is how
   * it keeps every member's runs on the trail. A personal workspace's runs are
   * never sent, whatever this says.
   */
  runTrailEnabled: boolean;
  /**
   * How often the background pass runs, in minutes — for every provider that is
   * on, since one timer walks all of their mappings.
   */
  taskSyncMinutes: number;

  /* Agent Preview (§5.18) */
  /**
   * Whether the agent in the sandbox may drive Preview itself — open the pages
   * its projects serve, click through them, screenshot them, read what they
   * logged, and send the project's saved calls (§5.18).
   *
   * **On by default**, unlike tunnels, and the difference is the boundary: a
   * tunnel puts a guest port on the public internet, while this reaches nothing
   * the guest could not already reach with `curl` on its own loopback. What it
   * adds is that the agent reaches it through Airlock's instruments, so what it
   * did is recorded where a person can look at it.
   */
  agentPreview: boolean;
  /**
   * Whether `browser_login` is among the tools — the agent asking the host to
   * sign a page in with a login this workspace saved (§5.11's vault).
   *
   * The password is read on the host and typed into a host-side page; it never
   * enters the sandbox and there is no tool that returns it. What does reach the
   * guest is a screenshot of the page *after* the login, which is the reason
   * this is a setting at all rather than simply part of the browser.
   */
  agentPreviewLogins: boolean;
  /**
   * Live probes (§5.23): whether the agent may watch a line of a running
   * project or of its own page, and how projects start for it. `attach` costs
   * nothing — Node gets an inspector port, and whatever already runs under a
   * debugger can be reached. `ready` also starts the JVM, Python and Go under
   * one and puts the debuggers in the session image, which costs each of them
   * some speed; that is why it is a choice. Rides the Preview bus, so it is off
   * wherever `agentPreview` is.
   */
  agentProbes: ProbeMode;

  /* Network (§7.1) — added to the deny-by-default allowlist */
  extraDomains: string[];
  /**
   * The resolvers a sandbox asks (`DEFAULT_NAMESERVERS`, §7.1).
   *
   * A setting rather than a constant because the two failures pull opposite
   * ways. Inheriting the host's resolvers breaks a VM that outlives the network
   * it booted on — the runtime copies them once, at the VM's start, and never
   * re-reads them, so a machine carried between offices leaves a fortnight-old
   * session unable to resolve anything while the host is fine. Pinning public
   * ones breaks the opposite case: a network that permits only its own
   * resolvers, which is ordinary in a company and on a captive portal.
   *
   * So the default is public and this is how a restricted network says
   * otherwise. Order is honoured: put the local resolvers first and keep the
   * public ones after them, and a guest asks the local pair where they answer
   * and the public pair where they do not.
   *
   * Empty means the default, not "no DNS" — a sandbox with no resolver reaches
   * nothing at all, and that is never what an empty list was meant to say.
   *
   * Application-wide, and deliberately not overridable per workspace: which
   * resolver answers is a property of the network this machine is on, and the
   * repositories being worked on have no opinion about it.
   *
   * It takes effect on sessions started afterwards. A live VM keeps the
   * resolvers it booted with, and reattaching does not change that.
   */
  nameservers: string[];
  /**
   * What happens to a host that is not on the allowlist (§7.1).
   *
   * `off` is the original boundary: the VM policy is the whole answer and an
   * unlisted host simply fails. `ask` and `auto` route the session's HTTP(S)
   * through the host-side gate so the decision can be made while the session
   * runs — `ask` holds the connection until the user answers, `auto` opens it
   * and reports what it opened. Neither can reach further than the gate allows:
   * private address space stays refused whatever the mode.
   */
  egressMode: EgressMode;

  /* Notifications (§12) */
  /**
   * How much this workspace's sessions may interrupt, as a banner from the
   * operating system (§12). The moments themselves are `@shared/notify`.
   *
   * Overridable, and one of the settings the layer was made for: the app value
   * is how you like to be interrupted, and a workspace answers for itself when
   * one of them matters more — or less — than the rest. A notice is suppressed
   * while the window has focus, in every mode: if you are looking at Airlock,
   * the interface has already told you.
   */
  notifications: NotifyMode;

  /* Logs (§5.11) */
  logRetentionMb: number;
  logBufferLines: number;
}

/**
 * The application settings a workspace may override (§4.1).
 *
 * What is deliberately *not* here is the point of the list. `confirmDestroy` is
 * a viewer preference: it describes the person, not the repositories.
 * `tunnelBinPath` names a file on this machine, which is a property of the
 * machine and of nothing else (§5.17). Everything else varies with the code
 * being worked on, so it belongs to the workspace.
 *
 * `logBufferLines` was in that first group and is not any more. How much output
 * a pane holds is not a taste: it is a property of what the processes being read
 * print, and a workspace of six chatty services needs a deeper pane than one
 * with a single quiet one (§5.11).
 *
 * `sandboxBackend` used to be excluded on the grounds that a workspace must
 * never be able to drop the isolation boundary. Placement (§5.19) is what
 * changed: dropping it is now an explicit per-session decision, recorded on the
 * session and shown for its whole life, so what this layer sets is the
 * **default the form starts from** — a workspace of Electron repositories that
 * no micro-VM can host should not have to be re-answered every time. The
 * session is still what decides, and still says so.
 *
 * `maxWorkingSessions` and `maxLiveSandboxes` were here and cannot be, now that
 * the board is one board and the ceilings count the whole application (§11.4).
 * A pool of three sessions shared by every workspace has one number by
 * definition; a per-workspace override of it would have described a share of a
 * pool nobody allocates, and the board would have shown one queue while
 * dispatching against several.
 */
export type OverridableSetting =
  | 'defaultModel'
  | 'defaultEffort'
  | 'defaultSourceBranch'
  | 'archiveRetentionDays'
  | 'sandboxBackend'
  | 'dockerfile'
  | 'containerRuntime'
  | 'vmCpu'
  | 'vmMemoryMb'
  | 'vmDiskGb'
  | 'dockerDiskGb'
  | 'autonomous'
  | 'agentConfigSource'
  | 'agentUpdate'
  | 'idleHintMs'
  | 'commitCoAuthoredBy'
  | 'autoSync'
  | 'gitEnabled'
  | 'tunnelsEnabled'
  | 'agentPreview'
  | 'agentPreviewLogins'
  | 'agentProbes'
  | 'egressMode'
  | 'notifications'
  | 'logRetentionMb'
  | 'logBufferLines'
  | 'agentExtensions'
  | 'privacyMask'
  | 'autoLand'
  | 'audit';

/**
 * The overrides a workspace configuration carries only when they are answered —
 * absent, not `null`, from one that inherits them (`SPARSE_CONFIG_KEYS`). Added
 * after installed apps had shipped a parser that refuses a key it does not
 * know, so a workspace that never answers one publishes exactly the document
 * it published before (`subPath`'s bargain, §5.25).
 */
export type SparseOverride = 'autoLand' | 'audit';

/**
 * The workspace layer, sparse on purpose: `null` means "inherit the application
 * value", so raising an app default still moves every workspace that never
 * disagreed with it.
 */
export type WorkspaceOverrides = { [K in Exclude<OverridableSetting, SparseOverride>]: AppSettings[K] | null } & {
  [K in SparseOverride]?: AppSettings[K] | null;
};

export interface WorkspaceConfig extends WorkspaceOverrides {
  workspaceId: string;
  /**
   * Egress hosts this workspace adds to the app allowlist. Additive, never a
   * replacement: a workspace can widen its own reach, it cannot quietly take
   * back a host the app decided to allow (§7.1).
   */
  extraDomains: string[];
  /**
   * Hosts refused without asking (§7.1). Deny-by-default already refuses them,
   * so this buys one thing: silence. A host answered "Block" once should not
   * interrupt again on the next connection, and a project that retries in a
   * loop should not be able to keep asking.
   *
   * Workspace-scoped on purpose — "this repository has no business calling that"
   * is a judgement about the code being worked on, not about the person.
   */
  blockedDomains: string[];
  /**
   * Services already running on the host — a database, a broker, a cache —
   * that this workspace's sessions may reach. Mode A of §5.9, and the one
   * deliberate hole in the isolation boundary: each one becomes a single egress
   * rule to one address on one port.
   *
   * Workspace-scoped only, for `blockedDomains`' reason and more strongly: no
   * application-wide default could be right, because a service belongs to the
   * repositories being worked on and to nothing else.
   */
  hostServices: HostService[];
  /**
   * How this workspace names its branches and writes its commit subjects
   * (§5.4, `git-formats.ts`) — `null` for no convention. Workspace-only for
   * `blockedDomains`' reason: a convention is the repositories' own.
   */
  gitFormats: GitFormats | null;
  /**
   * Containers each member's machine should be running for this workspace —
   * declared here, started by each member's Airlock on their click (§5.9.1).
   * Workspace-only for `hostServices`' reason, and not an override of anything:
   * no session reads it, so it is absent from `EffectiveSettings`.
   *
   * Present only when the workspace declares one: an install older than the key
   * refuses a document carrying a field it does not know (`parse.ts`), so a
   * workspace that declares none must publish exactly what it published before.
   */
  localServices?: LocalService[];
  /**
   * The workspace's rules repository (§5.27, `rules/repository.ts`): RFCs of
   * this project, cited `ws:N/slug`, applied together with the organization's.
   * Workspace-only for `gitFormats`' reason, and read by no session setting —
   * each machine fetches it with its own Git access. Present only when set, on
   * `localServices`' bargain with older installs; `null` in a patch removes it
   * (an absent key keeps what is stored) and is never kept.
   */
  rulesRepository?: RulesRepoRef | null;
  /**
   * Names and other terms this workspace's sessions mask (§7.5), **added** to
   * the application's own list (`privacyTerms`) — a client of these
   * repositories every member should hide, said once for the whole workspace.
   * Additive for `extraDomains`' reason: a workspace widens what is masked for
   * itself, it never takes back a name a person listed. Present only when it
   * holds one, on `localServices`' bargain with older installs.
   */
  privacyTerms?: string[];
  /**
   * The places a session's agent checks what it found besides Local — a
   * preprod, a production (`targets.ts`). Workspace-only: a target names this
   * workspace's projects and hosts. Present only when there is one, on
   * `localServices`' bargain with older installs.
   */
  targets?: WorkspaceTarget[];
}

/** An `AppSettings` with one workspace's layer applied — what the Supervisor acts on. */
export type EffectiveSettings = { [K in OverridableSetting]: AppSettings[K] } & {
  /** App hosts plus the workspace's own. */
  extraDomains: string[];
  /** Hosts this workspace refuses without asking (§7.1). */
  blockedDomains: string[];
  /**
   * Hosts the member's organizations block (`org/policy.ts`), each with the
   * organization that blocks it — a name and everything under it, refused
   * without asking and never allowed on this machine.
   */
  orgBlocked: { domain: string; org: string }[];
  /**
   * The agents and models the member's organizations let a run name
   * (`org/policy.ts`, `runRefusal`) — null where nothing narrows them. Read at
   * every place a run starts: a session, a step, a side agent, an agent's own.
   */
  orgAgents: MemberAgents | null;
  /**
   * The organizations' libraries a session's MCP servers, skills and plugins
   * are held to (`org/extension-library.ts`, `extensionRefusal`) — null where
   * no organization keeps an allowlist. Read where a session starts.
   */
  orgExtensions: MemberExtensions | null;
  /** Host services this workspace's sessions may reach (§5.9). */
  hostServices: HostService[];
  /** This workspace's branch and commit formats (§5.4). */
  gitFormats: GitFormats | null;
  /** The agents' own settings — the application's, since no workspace overrides them (§5.8). */
  agentSettings: AgentSettings;
  /** Claude Code's status line script — the application's, with the document it completes. */
  statusLineScript: string;
  /** What an agent writes for a person is asked in — the application's, for the same reason. */
  writingLanguages: WritingLanguages;
  /** How much history a session may carry in per repository — the application's, as above. */
  repoSeedMaxMb: number;
  /** Terms masked out of what is sent to an agent — the application's, plus the workspace's own (§7.5). */
  privacyTerms: string[];
  /** The workspace's targets (`targets.ts`), then those its organization shares (`sharedBy`); a session runs on one of them, or on Local. */
  targets: WorkspaceTarget[];
  /** What the member's organizations forbid of targets, each with the organization that does. */
  orgTargets: MemberTargets;
};
